import { readonly, ref, shallowRef, toValue, watch } from 'vue';
import { DateTime } from 'luxon';

import type { MaybeRefOrGetter, Ref } from 'vue';

type TickerEntry = {
  now: Ref<DateTime>;
  intervalId: ReturnType<typeof setInterval> | null;
  subscribers: number;
};

export const ONE_SECOND_INTERVAL = 1000; // in ms;
export const ONE_MIN_INTERVAL = 60000; // in ms;
export const FIVE_MIN_INTERVAL = 300000; // in ms;
export const TEN_MIN_INTERVAL = 600000; // in ms;
export const ONE_HOUR_INTERVAL = 3600000; // in ms;

const tickers = new Map<number, TickerEntry>();

function normalizeInterval(intervalMs: number | undefined): number {
  return Math.max(0, intervalMs ?? ONE_MIN_INTERVAL);
}

function createTicker(intervalMs: number): TickerEntry {
  const now = ref(DateTime.now());

  const intervalId =
    intervalMs > 0
      ? setInterval(() => {
          now.value = DateTime.now();
        }, intervalMs)
      : null;

  return {
    now,
    intervalId,
    subscribers: 0
  };
}

function subscribe(intervalMs: number): TickerEntry {
  let ticker = tickers.get(intervalMs);

  if (!ticker) {
    ticker = createTicker(intervalMs);
    tickers.set(intervalMs, ticker);
  }

  ticker.subscribers += 1;

  return ticker;
}

function unsubscribe(intervalMs: number): void {
  const ticker = tickers.get(intervalMs);

  if (!ticker) {
    return;
  }

  ticker.subscribers -= 1;

  if (ticker.subscribers > 0) {
    return;
  }

  if (ticker.intervalId) {
    clearInterval(ticker.intervalId);
  }

  tickers.delete(intervalMs);
}

/**
 * Provides a shared reactive DateTime updated by one timer per interval frequency.
 * Consumers with the same interval share the same timer.
 */
export function useElapsedTimeTicker(
  intervalMs: MaybeRefOrGetter<number | undefined> = ONE_MIN_INTERVAL,
  enabled: MaybeRefOrGetter<boolean> = true
) {
  const now = shallowRef(DateTime.now());

  watch(
    () => ({
      interval: normalizeInterval(toValue(intervalMs)),
      enabled: toValue(enabled)
    }),
    ({ interval, enabled }, _, onCleanup) => {
      if (!enabled) {
        now.value = DateTime.now();
        return;
      }

      const ticker = subscribe(interval);

      // A consumer joining mid-cycle reads the clock rather than inheriting the ticker's last tick,
      // which can be a whole interval old.
      now.value = DateTime.now();

      const stopTickerWatcher = watch(ticker.now, value => {
        now.value = value;
      });

      onCleanup(() => {
        stopTickerWatcher();
        unsubscribe(interval);
      });
    },
    { immediate: true }
  );

  return {
    now: readonly(now)
  };
}
